import * as yup from "yup";

export const AppSettingsSchema = () => {
    const schema = {
        texts: yup
            .array()
            .of(
                yup.object().shape({
                    language: yup.string().required(),
                    text: yup.string(),
                })
            )
            .test("at-least-one-text", "At least one text is required", (texts) => {
                return texts?.some((item: { text?: string }) => item.text && item.text.trim() !== "");
            })
            .required(),
        speed: yup.string().required(),
        enable_marquee: yup.boolean().required(),
    };

    return yup.object().shape(schema);
};

/**
 * Kiosk Configuration Settings Validation Schema
 */
export const kioskConfigSchema = () => {
    const schema = {
        general: yup.object().shape({
            logoUrl: yup.string().url("Invalid URL").nullable(),
            languageOptions: yup
                .array()
                .of(yup.string().required())
                .min(1, "At least one language is required")
                .required(),
            languageDisplayMode: yup.string().oneOf(["dropdown", "list"]).required(),
        }),
        advanced: yup.object().shape({
            enableCorrector: yup.boolean().required(),
        }),
    };

    return yup.object().shape(schema);
};

export const languageSettingsSchema = () => {
    const schema = {
        languageOptions: yup.array().of(yup.string().required()).min(1, "At least one language is required").required(),
        defaultLanguage: yup.string().required("Default language is required"),
        languageDisplayMode: yup.string().oneOf(["dropdown", "expand"]).required("Display mode is required"),
    };
    return yup.object().shape(schema);
};

/**
 * QR Code Settings Validation Schema
 * Validates QR code configuration with URL or image upload modes
 */
export const qrcodeValidationSchema = (t: (key: string, options?: Record<string, unknown>) => string) => {
    return yup.object().shape({
        QRcode_show_mode: yup.boolean().required(t("APPWIDGET.QRCODE_ENABLE_REQUIRED")),
        inputMode: yup.string().oneOf(["url", "image"]).required(),
        url: yup.string().when(["QRcode_show_mode", "inputMode"], {
            is: (enabled: boolean, mode: string) => enabled && mode === "url",
            then: (schema) =>
                schema
                    .required(t("APPWIDGET.QRCODE_URL_REQUIRED"))
                    .test("valid-url", t("APPWIDGET.QRCODE_URL_INVALID"), function (value) {
                        if (!value || value.trim() === "") {
                            return false;
                        }
                        try {
                            new URL(value);
                            return true;
                        } catch {
                            return this.createError({ message: t("APPWIDGET.QRCODE_URL_INVALID") });
                        }
                    }),
            otherwise: (schema) => schema.notRequired(),
        }),
        imageFile: yup.mixed<File>().nullable(),
        imageUrl: yup.string().when(["QRcode_show_mode", "inputMode"], {
            is: (enabled: boolean, mode: string) => enabled && mode === "image",
            then: (schema) =>
                schema.test("image-required", t("APPWIDGET.QRCODE_IMAGE_REQUIRED"), function () {
                    const { imageFile, imageUrl } = this.parent;
                    return Boolean(imageFile) || Boolean(imageUrl && imageUrl.trim() !== "");
                }),
            otherwise: (schema) => schema.notRequired(),
        }),
    });
};

/**
 * Festival Settings Validation Schema
 * Simple boolean validation for all festival setting toggles
 */
export const festivalValidationSchema = () => {
    return yup.object().shape({
        use_festival: yup.boolean().required(),
        bgm: yup.boolean().required(),
        bg: yup.boolean().required(),
        bg_obj: yup.boolean().required(),
        effect: yup.boolean().required(),
        character: yup.boolean().required(),
        character_is_conservative: yup.boolean().required(),
        filter: yup.boolean().required(),
        filter_keep_default: yup.boolean().required(),
    });
};

/**
 * Photo Filter Settings Validation Schema
 * Validates photo filter configuration with custom filters (max 3)
 * and photo settings parameters
 */
export const photoFilterValidationSchema = () => {
    const filterItemSchema = yup.object().shape({
        key: yup.string().required(),
        url: yup.string(), // URL can be empty for pending uploads
        file: yup.mixed<File>().nullable(),
    });

    const filterArraySchema = yup.array().of(filterItemSchema).max(3);

    return yup.object().shape({
        enabled_photo: yup.boolean().required(), // Main toggle for photo feature
        customized: yup.boolean().required(),
        customFilters: filterArraySchema,
        defaultFilters: yup.array().of(yup.string()), // Selected default filter keys (multi-select)
        // Photo settings parameters
        take_photo_sec: yup.number().min(1).max(30).required(),
        close_result_sec: yup.number().min(1).max(30).required(),
        character_enable: yup.boolean().required(),
        line_enabled: yup.boolean().required(),
    });
};

/**
 * Background Settings Validation Schema
 * Validates background image configuration for portrait and landscape orientations
 * Always uses separate images for both orientations
 */
export const backgroundValidationSchema = () => {
    const backgroundImageSchema = yup.object().shape({
        file: yup.mixed<File>().nullable(),
        url: yup.string(), // URL can be empty when using default
    });

    return yup.object().shape({
        portrait: backgroundImageSchema,
        landscape: backgroundImageSchema,
    });
};

/**
 * Volume/Voice Settings Validation Schema
 * Validates volume configuration with 0-1 range for all volume parameters
 */
export const volumeValidationSchema = () => {
    const volumeFieldSchema = yup.number().min(0, "音量不能小於 0").max(1, "音量不能大於 1").required("此欄位為必填");

    return yup.object().shape({
        bgm_file: yup.mixed<File>().nullable(),
        bgm_url: yup.string(),
        bgm_max: volumeFieldSchema,
        bgm_min: volumeFieldSchema,
        ad_video_max: volumeFieldSchema,
        ad_video_min: volumeFieldSchema,
        voice: volumeFieldSchema,
        effect: volumeFieldSchema,
    });
};

/**
 * Weather Settings Validation Schema
 * Validates weather display configuration with station selection
 */
export const weatherValidationSchema = () => {
    return yup.object().shape({
        enabled_weather: yup.boolean().required(),
        display_description: yup.boolean().required(),
        station_name: yup.string().when("enabled_weather", {
            is: true,
            then: (schema) => schema.required("請選擇氣象站"),
            otherwise: (schema) => schema.notRequired(),
        }),
        station_code: yup.string().when("enabled_weather", {
            is: true,
            then: (schema) => schema.required("氣象站代碼為必填"),
            otherwise: (schema) => schema.notRequired(),
        }),
    });
};

/**
 * Bidirectional Translation Settings Validation Schema
 * Validates bidirectional translation configuration with enabled toggle
 */
export const bidirectionalTranslationValidationSchema = () => {
    return yup.object().shape({
        enabled: yup.boolean().required(),
    });
};

/**
 * Interaction Settings Validation Schema
 * Validates interaction panel configuration with mode and multi-language content
 */
export const interactionValidationSchema = () => {
    return yup.object().shape({
        enabled_interaction: yup.boolean().required(),
        interaction_mode: yup.string().oneOf(["touch", "gesture", "custom"]).required(),
        data: yup.array().of(
            yup.object().shape({
                language: yup.string().required("語言為必填"),
                content: yup.string().required("提示內容為必填"),
            })
        ),
    });
};

/**
 * Layout Mode Settings Validation Schema
 * Validates layout mode selection (standard, guide, broadcast, section)
 * ad_position: "left" | "right" for broadcast mode, "top" | "bottom" for section mode
 */
export const layoutModeValidationSchema = () => {
    return yup.object().shape({
        mode: yup.string().oneOf(["standard", "guide", "broadcast", "section"]).required("請選擇切版模式"),
        ad_position: yup
            .string()
            .oneOf(["left", "right", "top", "bottom"])
            .when("mode", {
                is: (mode: string) => mode === "broadcast" || mode === "section",
                then: (s) => s.required("請選擇位置"),
                otherwise: (s) => s.optional(),
            }),
    });
};
